import type { MatchPerson, Stadium } from '@uefadigital/panenka';
import { PkAvatar, PkDivider, PkIcon, PkIdentifier } from '@uefadigital/panenka-react';
import { memo, useMemo } from 'react';

import Image from '../../Image';
import useMatch from '../../MatchUnit/useMatch';
import { useTranslateFn } from '../../Translate';

import { hydrateWithTranslations } from '@services/TranslatorService';
import type { Match } from '@shared-types/match.types';
import { dateToString } from '@utilities/dateTime';
import { isFriendly, isFutsal } from '@utilities/live';

import * as styles from './styles.module.scss';

const matchPersonRoleOrder = [
  'ASSISTANT_COACH',
  'COACH',
  'REFEREE',
  'ASSISTANT_REFEREE_ONE',
  'ASSISTANT_REFEREE_TWO',
  'ADDITIONAL_ASSISTANT_REFEREE_ONE',
  'ADDITIONAL_ASSISTANT_REFEREE_TWO',
  'VIDEO_ASSISTANT_REFEREE',
  'ASSISTANT_VIDEO_ASSISTANT_REFEREE',
  'ASSISTANT_VIDEO_ASSISTANT_REFEREE_ONE',
  'ASSISTANT_VIDEO_ASSISTANT_REFEREE_TWO',
  'ASSISTANT_VIDEO_ASSISTANT_REFEREE_THREE',
  'FOURTH_OFFICIAL',
  'REFEREE_ONE',
  'REFEREE_TWO',
  'REFEREE_THREE',
  'TIMEKEEPER',
  'REFEREE_OBSERVER',
  'UEFA_DELEGATE',
];

const roleOrderMap: Record<string, number> = matchPersonRoleOrder.reduce(
  (acc, role, index) => {
    acc[role] = index;
    return acc;
  },
  {} as Record<string, number>,
);

const getStadiumThumb = (id: string) => `${window.imgBaseUrl}/imgml/stadium/w1/${id}.jpg`;
const getRefHeadshot = (id: string) => `${window.imgBaseUrl}/imgml/referees/75x75/${id}.jpg`;

const hydrateReferee = (referee: MatchPerson) =>
  !!referee && {
    ...referee,
    ...referee?.person,
    name: hydrateWithTranslations(referee?.person)?.name,
    roleName: hydrateWithTranslations(referee)?.roleName,
  };

const MatchInfoOfficials = ({ match, referees }: { match: Match; referees: MatchPerson[] }) => {
  const t = useTranslateFn();
  const sortedFutsalReferees = useMemo(
    () =>
      referees
        .filter((r) => roleOrderMap[r.role?.toUpperCase()] !== undefined)
        .sort(
          (a, b) =>
            (roleOrderMap[a.role?.toUpperCase()] ?? Infinity) - (roleOrderMap[b.role?.toUpperCase()] ?? Infinity),
        ),
    [referees],
  );
  const referee = useMemo(() => hydrateReferee(referees?.find((r) => r.role?.toUpperCase() === 'REFEREE')), [referees]);
  const assReferee = useMemo(
    () => hydrateReferee(referees?.find((r) => r.role?.toUpperCase() === 'ASSISTANT_REFEREE_ONE')),
    [referees],
  );
  const assReferee2 = useMemo(
    () => hydrateReferee(referees?.find((r) => r.role?.toUpperCase() === 'ASSISTANT_REFEREE_TWO')),
    [referees],
  );
  const fourthOfficial = useMemo(
    () => hydrateReferee(referees?.find((r) => r.role?.toUpperCase() === 'FOURTH_OFFICIAL')),
    [referees],
  );

  const addAssistantReferee = useMemo(
    () => hydrateReferee(referees?.find((r) => r.role?.toUpperCase() === 'ADDITIONAL_ASSISTANT_REFEREE_ONE')),
    [referees],
  );
  const addAssistantReferee2 = useMemo(
    () => hydrateReferee(referees?.find((r) => r.role?.toUpperCase() === 'ADDITIONAL_ASSISTANT_REFEREE_TWO')),
    [referees],
  );

  const videoAssistantReferee = useMemo(
    () => hydrateReferee(referees?.find((r) => r.role?.toUpperCase() === 'VIDEO_ASSISTANT_REFEREE')),
    [referees],
  );
  const assVideoAssistantReferee = useMemo(
    () => hydrateReferee(referees?.find((r) => r.role?.toUpperCase() === 'ASSISTANT_VIDEO_ASSISTANT_REFEREE')),
    [referees],
  );

  if (!referees?.length) return null;

  if (isFutsal(match))
    return (
      <div className='officials-info-wrap'>
        {sortedFutsalReferees.map((r, index) => (
          <PkIdentifier key={r?.person?.id ?? index} className='pk-py--xs referee-info'>
            <span slot='primary' className='official-title'>
              {hydrateReferee(r)?.roleName}
            </span>
            <span slot='secondary' className='official'>
              {hydrateReferee(r)?.name} <span className='official-countrycode'>{hydrateReferee(r)?.countryCode}</span>
            </span>
          </PkIdentifier>
        ))}
      </div>
    );

  return (
    <>
      <div className='officials-info-wrap'>
        {referee && (
          <PkIdentifier className='pk-py--xs referee-info'>
            <span slot='prefix' className='pk-mr--m'>
              <PkAvatar
                className='referee-photo'
                fallback-image='generic-head'
                size={48}
                src={getRefHeadshot(referee.id)}
                type='image'
              />
            </span>
            <span slot='primary' className='official-title'>
              {referee.roleName}
            </span>
            <span slot='secondary' className='official'>
              {referee.name} <span className='official-countrycode'>{referee.countryCode}</span>
            </span>
          </PkIdentifier>
        )}
        {assReferee && (
          <PkIdentifier className='pk-py--xs other-info'>
            <span slot='primary' className='official-title'>
              {t('assistant_referees')}
            </span>
            <span slot='secondary'>
              <p className='official'>
                {assReferee.name} <span className='official-countrycode'>{assReferee.countryCode}</span>
              </p>
              {assReferee2 && (
                <p className='official'>
                  {assReferee2.name} <span className='official-countrycode'>{assReferee2.countryCode}</span>
                </p>
              )}
            </span>
          </PkIdentifier>
        )}
        {fourthOfficial && (
          <PkIdentifier className='pk-py--xs other-info'>
            <span slot='primary' className='official-title'>
              {fourthOfficial.roleName}
            </span>
            <span slot='secondary' className='official'>
              {fourthOfficial.name} <span className='official-countrycode'>{fourthOfficial.countryCode}</span>
            </span>
          </PkIdentifier>
        )}
        {addAssistantReferee && (
          <PkIdentifier className='pk-py--xs other-info'>
            <span slot='primary' className='official-title'>
              {addAssistantReferee.roleName}
            </span>
            <span slot='secondary'>
              <p className='official'>
                {addAssistantReferee.name}{' '}
                <span className='official-countrycode'>{addAssistantReferee.countryCode}</span>
              </p>
              {addAssistantReferee2 && (
                <p className='official'>
                  {addAssistantReferee2.name}{' '}
                  <span className='official-countrycode'>{addAssistantReferee2.countryCode}</span>
                </p>
              )}
            </span>
          </PkIdentifier>
        )}
        {videoAssistantReferee && (
          <PkIdentifier className='pk-py--xs other-info'>
            <span slot='primary' className='official-title'>
              {videoAssistantReferee.roleName}
            </span>
            <span slot='secondary' className='official'>
              {videoAssistantReferee.name}{' '}
              <span className='official-countrycode'>{videoAssistantReferee.countryCode}</span>
            </span>
          </PkIdentifier>
        )}
        {assVideoAssistantReferee && (
          <PkIdentifier className='pk-py--xs other-info'>
            <span slot='primary' className='official-title'>
              {assVideoAssistantReferee.roleName}
            </span>
            <span slot='secondary' className='official'>
              {assVideoAssistantReferee.name}{' '}
              <span className='official-countrycode'>{assVideoAssistantReferee.countryCode}</span>
            </span>
          </PkIdentifier>
        )}
      </div>
      <PkDivider />
    </>
  );
};

const StadiumInfo = ({ stadium }: { stadium: Stadium }) => (
  <div className='stadium-info'>
    <p className='match-info__subtitle'>{hydrateWithTranslations(stadium).name}</p>
    <p>
      {hydrateWithTranslations(stadium.city).name} ({stadium.countryCode ?? stadium.city.countryCode})
    </p>
  </div>
);

const MatchInfo = ({ options, isLiveblog }: { options: { match: Match }; isLiveblog?: boolean }) => {
  const t = useTranslateFn();
  const match = useMatch(options.match);

  return (
    <div className={`card-match card-match-info match-info__container ${styles.container}`}>
      {isLiveblog && <h3 className='match-info__title'>{t('match_info')}</h3>}
      {match.stadium && (
        <>
          <div className='stadium-info-wrap'>
            <Image
              className='stadium-thumb'
              src={`${getStadiumThumb(match.stadium.id)}`}
              fallback={`${window.imgBaseUrl}/imgml/uefacom/${window.competitionFolder}/stadium/fallback.jpg`}
            />
            <StadiumInfo stadium={match.stadium} />
          </div>
          <PkDivider />
        </>
      )}
      {!isFutsal(match) && match.condition && (
        <>
          <div className='weather-info-wrap'>
            {!!match.condition.weatherCondition && (
              <PkIcon
                className='weather-icon'
                name={`weather-${match.condition.weatherCondition.replaceAll('_', '-').toLowerCase()}`}
                size={36}
              />
            )}
            <div className='weather-info'>
              {Number.isInteger(match.condition.temperature) && (
                <span className='degrees'>{`${match.condition.temperature}°`}</span>
              )}
            </div>
            <div className='other-info'>
              {!!match.condition.weatherCondition && (
                <p className='weather-condition'>{hydrateWithTranslations(match.condition).weatherConditionName}</p>
              )}
              {!!match.condition.pitchCondition && (
                <p>
                  {t('pitch_label')}
                  &nbsp;
                  {hydrateWithTranslations(match.condition).pitchConditionName}
                </p>
              )}
              {match.condition.humidity > 0 && (
                <p>
                  {t('humidity')}
                  :&nbsp;{`${match.condition.humidity}%`}
                </p>
              )}
              {match.condition.windSpeed > 0 && (
                <p>
                  {t('wind')}
                  :&nbsp;{match.condition.windSpeed} {t('stats_km_h')}
                </p>
              )}
            </div>
          </div>
          <PkDivider />
        </>
      )}
      {match.referees && <MatchInfoOfficials match={match} referees={match.referees} />}
      <div className='match-info__time'>
        {!isFriendly(match) && (
          <div className='match-info__subtitle match-info__phase'>{hydrateWithTranslations(match.round)?.name}</div>
        )}
        {match.kickOffTime && (
          <div className='match-info__date'>
            {dateToString(
              match.kickOffTime?.dateTime ?? match.kickOffTime?.date,
              match.kickOffTime?.dateTime ? 'dddd, D MMM YYYY • HH:mm' : 'dddd, D MMM YYYY',
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(MatchInfo);
